/**
 * Main JavaScript file that handles app's interactions and functionality on the front-end.
 * This file uses revealing pattern design to prevent polluting the scope with variables
 * and to prevent malicious users from accessing methods in the Developer Tools.
 * 
 * @author Chris
 * @created 29 September 2022
 */


var app = (function () {
    "use strict";


    /**
     * [private] Module properties
     */
    const body = document.querySelector('body')
    const head = document.querySelector('head')


    /**
     * [private] Method to calculate the absolute height of a given DOM Element
     * 
     * @param DOMElement
     * @return Array
     */
    function __absoluteHeight(elem) {
        const height = elem.offsetHeight,
              style = window.getComputedStyle(elem)

        return ['top', 'bottom']
            .map(side => parseInt(style[`margin-${side}`]))
            .reduce((total, side) => total + side, height)
    }


    /**
     * [private] Method that sets the header stickiness
     * 
     * @return Void
     */
    function __stickyHeader() {
        const header = document.getElementById('header')

        window.addEventListener('scroll', e => {
            header.classList.toggle('sticky', window.scrollY > __absoluteHeight(header))
        })
    }


    /**
     * [private] Method to initializes the Splide slider
     * 
     * @return Void
     */
    function __slider() {
        if (document.querySelector('.splide')) {

            const asset = document.createElement('script')
                  asset.src = '/js/vendor/splide.min.js'

            body.append(asset)

            asset.onload = function () {
                new Splide('.splide', {
                    perPage: 3,
                    gap: '1.5rem',
                    arrows: true,
                    pagination: false,
                    breakpoints: {
                        1180: {
                            perPage: 2,
                        },
                        768: {
                            perPage: 1,
                        }
                    }
                }).mount();
            }

        }
    }


    /**
     * [private] Method that initializes the parallax effect on images
     * 
     * @return Void
     */
    function __parallax() {
        if (document.querySelector('.parallax-container')) {

            const asset = document.createElement('script')
                  asset.src = '/js/vendor/simpleParallax.min.js'
            
            body.append(asset)

            asset.onload = function () {
                new simpleParallax(document.querySelectorAll('.parallax-container img'), {
                    customWrapper: '.parallax-container',
                    scale: 1.4,
                })
            }
            
        }
    }


    /**
     * [private] Date helper function
     */
    function __todayDate() {
        const today = new Date()
        const dd = String(today.getDate()).padStart(2, '0')
        const mm = String(today.getMonth() + 1).padStart(2, '0')
        const yyyy = today.getFullYear()

        return `${yyyy}-${mm}-${dd}`
    }


    /**
     * [private] Datepicker
     */
    function __datepicker() {
        if (document.querySelector('[data-input="datepicker"]')) {

            /**
             * Create stylesheet asset
             */
            const asset1 = document.createElement('link')
                  asset1.href = '/css/vendor/flatpickr.min.css'
                  asset1.rel = 'stylesheet'

            /**
             * Create JavaScript asset
             */
            const asset2 = document.createElement('script')
                  asset2.src = '/js/vendor/flatpickr.min.js'

            /**
             * Append stylesheet
             */
            head.append(asset1)

            /**
             * Load stylesheet
             */
            asset1.onload = function () {

                /**
                 * Append JavaScript
                 */
                body.append(asset2)

                /**
                 * Load JavaScript
                 */
                asset2.onload = function () {
                    const today = new Date(__todayDate()).getTime()

                    document.querySelectorAll('[data-input="datepicker"]').forEach(function (datepicker) {
                        datepicker.flatpickr({
                            disable: [
                                function (date) {
                                    return (date.getDay() === 0 || date.getDay() === 6)
                                }
                            ],
                            locale: {
                                firstDayOfWeek: 1
                            },
                            dateFormat: 'd/m/Y',
                            defaultDate: __todayDate(),
                            minDate: __todayDate(),
                        })
                    })
                }
            }
        }
    }


    /**
     * [private] Timepicker
     */
    function __timepicker() {
        if (document.querySelector('[data-toggle-input]')) {
            const toggles = document.querySelectorAll('[data-toggle-input]');

            toggles.forEach((elem, key) => {
                elem.addEventListener('click', (e) => {
                    e.preventDefault()

                    toggles.forEach((elem, key) => {
                        elem.classList.remove('active')
                    })

                    if (document.querySelector('[data-input]:checked')) {
                        document.querySelector('[data-input]').checked = false
                    }

                    if (!document.querySelector('[data-input="'+e.target.dataset.toggleInput+'"]:checked')) {
                        document.querySelector('[data-input="'+e.target.dataset.toggleInput+'"]').checked = true
                        elem.classList.add('active')
                    }

                    if (document.querySelector('[data-error="'+e.target.name+'"]')) {
                        document.querySelector('[data-error="'+e.target.name+'"]').style.opacity = 0
                    }
                })
            })
        }
    }


    /**
     * [private] Method to hide form success overlay
     */
    function __hideFormSuccess() {
        if (document.querySelector('[data-hide="form-success"]')) {
            document.querySelector('[data-hide="form-success"]').addEventListener('click', e => {
                
                if (document.querySelector('[data-name="form-success"]')) {
                    document.querySelector('[data-name="form-success"]').classList.add('hide')
                }
            })
        }
    }


    /**
     * [private] Form validator
     */
    function __validator() {
        __hideFormSuccess()

        if (document.querySelector('input')) {
            const general = document.querySelector('[data-errors="true"]')
            const formFields = [...document.querySelectorAll('input'), ...document.querySelectorAll('textarea')]

            formFields.forEach((input, key) => {
                input.addEventListener('change', (e) => {

                    if (document.querySelector('[data-error="'+e.target.name+'"]')) {
                        document.querySelector('[data-error="'+e.target.name+'"]').style.opacity = 0
                        document.querySelector('[data-error="'+e.target.name+'"]').setAttribute('data-correct', 'true')
                    }

                    general.style.opacity = 0
                })
            })
        }
    }

    /**
     * [private] Toggles
     */
    function __toggle() {
        const toggles = document.querySelectorAll('[data-toggle]')
        const elements = document.querySelectorAll('[data-element]')

        toggles.forEach((toggle, idx) => {
            let active = false

            toggle.addEventListener('click', evt => {

                elements.forEach((element, idx) => {
                    if (element.dataset.element == evt.target.dataset.toggle) {
                        element.classList.toggle('active')
                        toggle.classList.toggle('active')
                        active = !active

                        try {
                            /**
                             * Get the element text height
                             */
                            let height = __absoluteHeight(element.querySelector('[data-element-text]'))
                            
                            /**
                             * Set height of the element
                             */
                            element.style.height = active ? (height + 'px') : 0
                        }
                        catch {}
                    }
                })
            })
        })
    }

    function __scrollTop() {
        var scrollTop = document.querySelector('[data-name="scroll-top"]');
    
        scrollTop.addEventListener('click', function (e) {
            e.preventDefault();
    
            scrollTo({
                top: 0,
                left: 0,
                behavior: 'smooth'
            });
        });
    
        document.addEventListener('scroll', function (e) {
            
            if (window.scrollY >= 100) {
                scrollTop.classList.add('show');
            }
            else {
                scrollTop.classList.remove('show');
            }
        })
    }

    /**
     * Scroll to section
     */
    function __scrollTo() {
        if (document.querySelector('[data-scroller]')) {
            var scroller = document.querySelector('[data-scroller]');

            scroller.addEventListener('click', function (e) {
                e.preventDefault()

                var scrollTop = document.getElementById('intro').getBoundingClientRect().top

                scrollTo({
                    top: scrollTop,
                    left: 0,
                    behavior: 'smooth'
                })
            })
        }
    }

    /**
     * Toggle 2nd Applicant
     */
    function __toggle2ndApplicant() {
        if (document.querySelector('[data-toggle="add_2nd_applicant"]')) {
            document.querySelector('[data-toggle="add_2nd_applicant"]').addEventListener('click', function (e) {
                document.querySelectorAll('[data-applicant="2"]').forEach(function (field, _) {
                    field.classList.add('form__field--enabled');
                    field.querySelectorAll('[tabindex="-1"]').forEach(function (input, _) {
                        input.removeAttribute('tabindex');
                    });
                    e.target.style.display = 'none';
                    document.querySelector('[data-toggle="remove_2nd_applicant"]').style.display = 'inline-block';
                    document.querySelector('[data-hidden="add_2nd_applicant"]').value = 1;
                });
            });
        }
        if (document.querySelector('[data-toggle="remove_2nd_applicant"]')) {
            document.querySelector('[data-toggle="remove_2nd_applicant"]').addEventListener('click', function (e) {
                document.querySelectorAll('[data-applicant="2"]').forEach(function (field, _) {
                    field.classList.remove('form__field--enabled');
                    field.querySelectorAll('input').forEach(function (input, _) {
                        input.setAttribute('tabindex', '-1');
                    });
                    e.target.style.display = 'none';
                    document.querySelector('[data-toggle="add_2nd_applicant"]').style.display = 'inline-block';
                    document.querySelector('[data-hidden="add_2nd_applicant"]').value = 0;
                });
            });
        }
    }


    /**
     * Play promo video
     */
    function __playPromoVideo() {
        if (document.querySelector('[data-play="promo_video"]')) {
            document.querySelector('[data-play="promo_video"]').addEventListener('click', function (e) {
                e.preventDefault()
                var video = e.target.dataset.play;
                document.querySelector('[data-curtain="' + video + '"]').classList.add('hide')
                document.querySelector('[data-video="' + video + '"]').play()
            })
        }
    }


    /**
     * [public] Method to initialize app functionality in the right order
     * 
     * @return Void
     */
    function init(opt) {
        if (opt.stickyHeader !== undefined && opt.stickyHeader === true) __stickyHeader()
        if (opt.slider !== undefined && opt.slider === true) __slider()
        if (opt.parallax !== undefined && opt.parallax === true) __parallax()
        if (opt.datepicker !== undefined && opt.datepicker === true) __datepicker()
        if (opt.timepicker !== undefined && opt.timepicker === true) __timepicker()
        if (opt.validator !== undefined && opt.validator === true) __validator()
        if (opt.toggle !== undefined && opt.toggle == true) __toggle()
        if (opt.scrollTop !== undefined && opt.scrollTop == true) __scrollTop()
        if (opt.scrollTo !== undefined && opt.scrollTo == true) __scrollTo()
        if (opt.toggle2ndApplicant !== undefined && opt.toggle2ndApplicant == true) __toggle2ndApplicant()
        if (opt.playPromoVideo !== undefined && opt.playPromoVideo == true) __playPromoVideo()
    }


    /**
     * Expose public methods
     */
    return {
        init: init
    }


})();

/**
 * Load the app when window is ready
 */
window.load = app.init({
    slider: true,
    datepicker: true,
    timepicker: true,
    validator: true,
    toggle: true,
    scrollTop: true,
    scrollTo: true,
    toggle2ndApplicant: true,
    playPromoVideo: true,
})